import React, { useState, useEffect } from 'react';
import { API_LINK } from '../../apiLink/apiLink';
import Layout from '../../components/Layout';

const StudentForm = ({ onNavigate, userInfo }) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1024);
  const [selectedCategory, setSelectedCategory] = useState('seasonal');
  const [searchTerm, setSearchTerm] = useState('');

  // API Data States
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(null);

  // Student and Forms Data
  const [studentData, setStudentData] = useState({
    name: "Loading...",
    studentId: "...",
    profileImage: null
  });

  const [formsData, setFormsData] = useState({
    seasonal: [],
    academic: []
  });

  const [seasonalFormsSubmissions, setSeasonalFormsSubmissions] = useState({});

  // Check if mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 1024);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Fetch Student Forms Data from API
  useEffect(() => {
    const fetchFormsData = async () => {
      try {
        setLoading(true);
        setError(null);

        console.log('🔍 Starting API call to fetch forms data...');

        // Get token from storage
        const token = localStorage.getItem('token') || sessionStorage.getItem('token');

        if (!token) {
          console.log('❌ No token found');
          setError('Authentication token not found. Please login again.');
          setStudentData(prev => ({
            ...prev,
            name: userInfo?.name || "Student User",
            studentId: userInfo?.id || "SVU2024001"
          }));
          setLoading(false);
          return;
        }

        // First get student profile for basic info
        try {
          const profileResponse = await fetch(`${API_LINK}/api/student/profile`, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });

          if (profileResponse.ok) {
            const profileResult = await profileResponse.json();
            if (profileResult.success) {
              setStudentData({
                name: profileResult.data.name,
                studentId: profileResult.data.studentId,
                profileImage: profileResult.data.profileImage
              });
            }
          }
        } catch (profileError) {
          console.error('Error fetching profile:', profileError);
        }

        // Fetch seasonal forms data from database
        try {
          const seasonalResponse = await fetch(`${API_LINK}/api/student/seasonal-forms`, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });

          if (seasonalResponse.ok) {
            const seasonalResult = await seasonalResponse.json();
            if (seasonalResult.success) {
              setFormsData(prev => ({
                ...prev,
                seasonal: seasonalResult.data.forms || []
              }));
              setSeasonalFormsSubmissions(seasonalResult.data.submissions || {});
            }
          } else {
            // Empty seasonal forms if API fails
            setFormsData(prev => ({
              ...prev,
              seasonal: []
            }));
          }
        } catch (seasonalError) {
          console.error('Error fetching seasonal forms:', seasonalError);
          setFormsData(prev => ({
            ...prev,
            seasonal: []
          }));
        }

        // Static Academic Forms Data - Updated to match public directory
        const staticAcademicForms = [
          {
            id: 'bonafide-certificate',
            name: 'Bonafide Certificate',
            description: 'Student identity verification certificate for various purposes',
            size: '1.2 MB',
            lastUpdated: '15 Jan 2025',
            fileName: 'bonafide-certificate-form.pdf',
            category: 'academic',
            instructions: 'Fill this form to request a bonafide certificate. Processing time: 3-5 working days.',
            required_documents: 'Student ID card, Fee receipt'
          },
          {
            id: 'admission-form',
            name: 'Admission Application Form',
            description: 'New student admission application form',
            size: '2.5 MB',
            lastUpdated: '10 Jan 2025',
            fileName: 'admission-application-form.pdf',
            category: 'academic',
            instructions: 'Complete admission form for new academic session. Submit with all required documents.',
            required_documents: 'Academic certificates, ID proof, Photographs, Category certificate (if applicable)'
          },
          {
            id: 'library-membership',
            name: 'Library Membership Form',
            description: 'Register for library membership and book borrowing facility',
            size: '1.8 MB',
            lastUpdated: '08 Jan 2025',
            fileName: 'library-membership-form.pdf',
            category: 'academic',
            instructions: 'Submit this form to get library membership. Requires security deposit.',
            required_documents: 'Student ID card, Fee receipt, Passport size photo'
          }
        ];

        setFormsData(prev => ({
          ...prev,
          academic: staticAcademicForms
        }));

        console.log('✅ Forms data updated successfully');

      } catch (error) {
        console.error('💥 Error fetching forms data:', error);
        setError(`Failed to load forms data: ${error.message}`);

        // Fallback to userInfo if available
        if (userInfo) {
          console.log('🔄 Using fallback userInfo data');
          setStudentData(prev => ({
            ...prev,
            name: userInfo.name || prev.name,
            studentId: userInfo.id || prev.studentId
          }));
        }
      } finally {
        setLoading(false);
      }
    };

    // Add a small delay to ensure component is mounted
    const timer = setTimeout(fetchFormsData, 500);
    return () => clearTimeout(timer);
  }, [userInfo]);

  // FOOLPROOF DOWNLOAD FUNCTION - Works with actual files in public directory
  const handleDownload = async (form, category) => {
    try {
      setDownloading(form.id);

      console.log(`📥 Downloading ${form.name}...`);

      // Direct download from public directory
      const filePath = `/forms/${category}/${form.fileName}`;

      // Create invisible download link
      const downloadLink = document.createElement('a');
      downloadLink.href = filePath;
      downloadLink.download = form.fileName;
      downloadLink.style.display = 'none';

      // Add to DOM, click, and remove
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);

      console.log(`✅ Download initiated for ${form.name}`);

      // Show success message after short delay
      setTimeout(() => {
        alert(`📄 ${form.name} download started! Check your downloads folder.`);
      }, 500);

    } catch (error) {
      console.error('❌ Download failed:', error);
      alert(`❌ Failed to download ${form.name}. Please try again or contact support.`);
    } finally {
      setDownloading(null);
    }
  };





  // Form categories - Only Academic and Seasonal
  const categories = [
    { id: 'seasonal', name: 'Seasonal Forms', icon: '🕒', color: '#ff6b6b', description: 'Time-sensitive forms created by HOD' },
    { id: 'academic', name: 'Academic Forms', icon: '📚', color: '#238548', description: 'Permanent academic forms and certificates' }
  ];

  // Handle seasonal form submission
  const handleSeasonalFormAction = async (form) => {
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      if (!token) {
        alert('Authentication required. Please login again.');
        return;
      }

      const isSubmitted = seasonalFormsSubmissions[form.id];

      if (form.google_form_link) {
        // Open Google form in new tab
        window.open(form.google_form_link, '_blank');

        // Mark as submitted in our system
        if (!isSubmitted) {
          try {
            const response = await fetch(`${API_LINK}/api/student/seasonal-forms/submit`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                form_id: form.id,
                google_form_response_id: 'pending'
              })
            });

            if (response.ok) {
              setSeasonalFormsSubmissions(prev => ({
                ...prev,
                [form.id]: {
                  status: 'submitted',
                  submission_date: new Date().toISOString()
                }
              }));
              alert('Form opened in new tab. Please fill and submit the Google form.');
            }
          } catch (error) {
            console.error('Error recording submission:', error);
            alert('Form opened in new tab. Please fill and submit the Google form.');
          }
        } else {
          alert('You have already submitted this form. Check your status in the submissions section.');
        }
      } else {
        // Handle offline form download/submission
        alert(`📥 ${form.form_name} form will be downloaded. Please fill it and submit to the administration office.`);
      }

    } catch (error) {
      console.error('Seasonal form action error:', error);
      alert('Failed to process form. Please try again.');
    }
  };

  // Filter forms based on search term
  const getFilteredForms = (categoryForms) => {
    if (!searchTerm) return categoryForms;

    if (selectedCategory === 'seasonal') {
      return categoryForms.filter(form =>
        form.form_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        form.form_description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    } else {
      return categoryForms.filter(form =>
        form.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        form.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
  };



  // University Logo Component
  const SVULogo = () => (
    <img
      src="/logo.png"
      alt="Swami Vivekananda University Logo"
      style={{
        width: isMobile ? '40px' : '120px',
        height: isMobile ? '40px' : '120px',
        objectFit: 'contain',
        transition: 'transform 0.3s ease'
      }}
      onMouseEnter={(e) => {
        e.target.style.transform = 'scale(1.05)';
      }}
      onMouseLeave={(e) => {
        e.target.style.transform = 'scale(1)';
      }}
    />
  );

  // Loading Component
  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#f4f7fa',
        flexDirection: 'column',
        gap: '1rem'
      }}>
        <div style={{
          width: '50px',
          height: '50px',
          border: '4px solid #e3f2fd',
          borderTop: '4px solid #238548',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
        <p style={{ color: '#666', fontSize: '1.1rem' }}>Loading Forms...</p>
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    );
  }

  // Error Component
  if (error && !studentData.name) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#f4f7fa',
        flexDirection: 'column',
        gap: '1rem',
        padding: '2rem'
      }}>
        <div style={{
          backgroundColor: '#ffffff',
          padding: '3rem',
          borderRadius: '12px',
          boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
          textAlign: 'center',
          maxWidth: '400px',
          border: '1px solid #e0e0e0'
        }}>
          <div style={{
            fontSize: '4rem',
            color: '#ff6b6b',
            marginBottom: '1.5rem'
          }}>
            ⚠️
          </div>

          <h3 style={{
            color: '#333',
            margin: '0 0 1rem 0',
            fontSize: '1.4rem',
            fontWeight: 600
          }}>
            Unable to Load Forms
          </h3>

          <p style={{
            color: '#666',
            margin: '0 0 2rem 0',
            fontSize: '1rem',
            lineHeight: 1.5
          }}>
            For any further information, please contact your support team.
          </p>

          <div style={{
            display: 'flex',
            gap: '1rem',
            justifyContent: 'center',
            flexWrap: 'wrap'
          }}>
            <button
              onClick={() => window.location.reload()}
              style={{
                backgroundColor: '#238548',
                color: 'white',
                border: 'none',
                padding: '0.75rem 1.5rem',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: 500
              }}
            >
              Try Again
            </button>

            <button
              onClick={() => window.location.href = '/'}
              style={{
                backgroundColor: '#666',
                color: 'white',
                border: 'none',
                padding: '0.75rem 1.5rem',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: 500
              }}
            >
              Logout
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Layout role="student"
      onNavigate={onNavigate}
      activePage="student-forms"
      pageTitle="Forms Download"
    >

      {/* Forms Header */}
      <div style={{
        background: '#ffffff',
        borderRadius: '12px',
        boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
        marginBottom: '2rem',
        overflow: 'hidden'
      }}>
        <div style={{
          background: '#238548',
          color: '#ffffff',
          padding: '2rem',
          textAlign: 'center'
        }}>
          <h1 style={{
            margin: '0 0 0.5rem 0',
            fontSize: '2rem',
            fontWeight: 'bold'
          }}>
            📄 Forms Download Center
          </h1>
          <p style={{
            margin: 0,
            fontSize: '1.1rem',
            opacity: 0.9
          }}>
            Download official forms for academic purposes and seasonal submissions
          </p>
        </div>

        {/* Search Bar */}
        <div style={{ padding: '1.5rem' }}>
          <input
            type="text"
            placeholder="🔍 Search forms..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              padding: '0.75rem 1rem',
              border: '2px solid #ecf0f1',
              borderRadius: '8px',
              fontSize: '1rem',
              outline: 'none',
              transition: 'border-color 0.3s'
            }}
            onFocus={(e) => {
              e.target.style.borderColor = '#238548';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = '#ecf0f1';
            }}
          />
        </div>
      </div>

      {/* Category Tabs - Only 2 Categories */}
      <div style={{
        background: '#ffffff',
        borderRadius: '12px',
        boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
        marginBottom: '2rem',
        overflow: 'hidden'
      }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: '0'
        }}>
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => setSelectedCategory(category.id)}
              style={{
                background: selectedCategory === category.id ? category.color : '#f8f9fa',
                color: selectedCategory === category.id ? '#ffffff' : '#495057',
                border: 'none',
                padding: '2rem',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: '500',
                transition: 'all 0.3s',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '0.5rem',
                position: 'relative',
                textAlign: 'center'
              }}
            >
              <span style={{ fontSize: '2rem' }}>{category.icon}</span>
              <span style={{ fontSize: '1.1rem', fontWeight: '600' }}>{category.name}</span>
              <span style={{ fontSize: '0.85rem', opacity: 0.8 }}>{category.description}</span>
              {/* Badge for seasonal forms count */}
              {category.id === 'seasonal' && formsData.seasonal && formsData.seasonal.length > 0 && (
                <div style={{
                  position: 'absolute',
                  top: '12px',
                  right: '12px',
                  background: '#ff4757',
                  color: 'white',
                  borderRadius: '50%',
                  width: '24px',
                  height: '24px',
                  fontSize: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 'bold'
                }}>
                  {formsData.seasonal.filter(f => f.is_enabled).length}
                </div>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Seasonal Forms Special Notice */}
      {selectedCategory === 'seasonal' && (
        <div style={{
          background: 'linear-gradient(135deg, #ff6b6b 0%, #ff8e53 100%)',
          color: '#ffffff',
          borderRadius: '12px',
          padding: '1.5rem',
          marginBottom: '2rem',
          boxShadow: '0 4px 15px rgba(255, 107, 107, 0.3)'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '1rem',
            marginBottom: '1rem'
          }}>
            <span style={{ fontSize: '2rem' }}>🕒</span>
            <div>
              <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1.3rem' }}>
                Seasonal Forms - Time Sensitive
              </h3>
              <p style={{ margin: 0, opacity: 0.9 }}>
                These forms are created and enabled by HOD for specific periods. Submit within the deadline.
              </p>
            </div>
          </div>
          <div style={{
            background: 'rgba(255, 255, 255, 0.1)',
            borderRadius: '8px',
            padding: '1rem',
            fontSize: '0.9rem'
          }}>
            <strong>Important:</strong> Some forms redirect to Google Forms for online submission,
            while others need to be downloaded, filled, and submitted manually to the administration.
          </div>
        </div>
      )}

      {/* Academic Forms Special Notice */}
      {selectedCategory === 'academic' && (
        <div style={{
          background: 'linear-gradient(135deg, #238548 0%, #97d6ac 100%)',
          color: '#ffffff',
          borderRadius: '12px',
          padding: '1.5rem',
          marginBottom: '2rem',
          boxShadow: '0 4px 15px rgba(35, 133, 72, 0.3)'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '1rem',
            marginBottom: '1rem'
          }}>
            <span style={{ fontSize: '2rem' }}>📚</span>
            <div>
              <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1.3rem' }}>
                Academic Forms - Official Documents
              </h3>
              <p style={{ margin: 0, opacity: 0.9 }}>
                Download official academic forms and certificates. Available throughout the academic year.
              </p>
            </div>
          </div>
          <div style={{
            background: 'rgba(255, 255, 255, 0.1)',
            borderRadius: '8px',
            padding: '1rem',
            fontSize: '0.9rem'
          }}>
            <strong>Note:</strong> These forms need to be filled completely and submitted to the respective department
            along with required documents. Processing time varies for each form type.
          </div>
        </div>
      )}

      {/* Forms Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(350px, 1fr))',
        gap: '1.5rem'
      }}>
        {selectedCategory === 'seasonal' ? (
          // Render seasonal forms
          getFilteredForms(formsData[selectedCategory]).filter(form => form.is_enabled).length > 0 ? (
            getFilteredForms(formsData[selectedCategory]).filter(form => form.is_enabled).map((form, index) => {
              const isSubmitted = seasonalFormsSubmissions[form.id];
              const currentDate = new Date();
              const startDate = form.start_date ? new Date(form.start_date) : null;
              const endDate = form.end_date ? new Date(form.end_date) : null;
              const isActive = (!startDate || currentDate >= startDate) && (!endDate || currentDate <= endDate);

              return (
                <div key={form.id} style={{
                  background: '#ffffff',
                  borderRadius: '12px',
                  boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                  overflow: 'hidden',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                  opacity: isActive ? 1 : 0.7,
                  border: isSubmitted ? '2px solid #28a745' : form.google_form_link ? '2px solid #007bff' : '2px solid #dc3545'
                }}
                  onMouseEnter={(e) => {
                    if (isActive) {
                      e.currentTarget.style.transform = 'translateY(-5px)';
                      e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.15)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 15px rgba(0,0,0,0.1)';
                  }}>
                  {/* Form Header */}
                  <div style={{
                    background: isSubmitted ? '#28a745' : form.google_form_link ? '#007bff' : '#dc3545',
                    color: '#ffffff',
                    padding: '1.5rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '1rem'
                    }}>
                      <span style={{ fontSize: '1.5rem' }}>
                        {isSubmitted ? '✅' : form.google_form_link ? '🌐' : '📄'}
                      </span>
                      <h3 style={{ margin: 0, fontSize: '1.1rem' }}>{form.form_name}</h3>
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                      <span style={{
                        padding: '0.25rem 0.75rem',
                        borderRadius: '20px',
                        fontSize: '0.7rem',
                        fontWeight: '500',
                        backgroundColor: 'rgba(255, 255, 255, 0.2)',
                        color: '#ffffff'
                      }}>
                        {form.google_form_link ? 'ONLINE' : 'OFFLINE'}
                      </span>
                      <span style={{
                        padding: '0.25rem 0.75rem',
                        borderRadius: '20px',
                        fontSize: '0.7rem',
                        fontWeight: '500',
                        backgroundColor: 'rgba(255, 255, 255, 0.2)',
                        color: '#ffffff'
                      }}>
                        Priority: {form.priority}
                      </span>
                    </div>
                  </div>

                  {/* Form Content */}
                  <div style={{ padding: '1.5rem' }}>
                    <p style={{
                      margin: '0 0 1rem 0',
                      color: '#666',
                      fontSize: '0.95rem',
                      lineHeight: 1.5
                    }}>
                      {form.form_description}
                    </p>

                    {/* Instructions */}
                    {form.instructions && (
                      <div style={{
                        background: '#f8f9fa',
                        padding: '1rem',
                        borderRadius: '8px',
                        marginBottom: '1rem',
                        border: '1px solid #e9ecef'
                      }}>
                        <h4 style={{ margin: '0 0 0.5rem 0', color: '#495057', fontSize: '0.9rem' }}>
                          📋 Instructions:
                        </h4>
                        <p style={{ margin: 0, fontSize: '0.85rem', color: '#6c757d' }}>
                          {form.instructions}
                        </p>
                      </div>
                    )}

                    {/* Required Documents */}
                    {form.required_documents && (
                      <div style={{
                        background: '#fff3cd',
                        padding: '1rem',
                        borderRadius: '8px',
                        marginBottom: '1rem',
                        border: '1px solid #ffeaa7'
                      }}>
                        <h4 style={{ margin: '0 0 0.5rem 0', color: '#856404', fontSize: '0.9rem' }}>
                          📎 Required Documents:
                        </h4>
                        <p style={{ margin: 0, fontSize: '0.85rem', color: '#856404' }}>
                          {form.required_documents}
                        </p>
                      </div>
                    )}

                    {/* Dates */}
                    {(form.start_date || form.end_date) && (
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: '1rem',
                        marginBottom: '1.5rem'
                      }}>
                        {form.start_date && (
                          <div>
                            <span style={{
                              display: 'block',
                              fontSize: '0.8rem',
                              color: '#999',
                              marginBottom: '0.25rem'
                            }}>
                              Start Date
                            </span>
                            <span style={{
                              fontSize: '0.9rem',
                              fontWeight: '500',
                              color: '#333'
                            }}>
                              {new Date(form.start_date).toLocaleDateString()}
                            </span>
                          </div>
                        )}
                        {form.end_date && (
                          <div>
                            <span style={{
                              display: 'block',
                              fontSize: '0.8rem',
                              color: '#999',
                              marginBottom: '0.25rem'
                            }}>
                              End Date
                            </span>
                            <span style={{
                              fontSize: '0.9rem',
                              fontWeight: '500',
                              color: endDate && currentDate > endDate ? '#dc3545' : '#28a745'
                            }}>
                              {new Date(form.end_date).toLocaleDateString()}
                            </span>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Submission Status */}
                    {isSubmitted && (
                      <div style={{
                        background: '#d4edda',
                        color: '#155724',
                        padding: '1rem',
                        borderRadius: '8px',
                        marginBottom: '1rem',
                        border: '1px solid #c3e6cb'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <span>✅</span>
                          <strong>Submitted on:</strong>
                          <span>{new Date(isSubmitted.submission_date).toLocaleDateString()}</span>
                        </div>
                        <div style={{ fontSize: '0.9rem', marginTop: '0.5rem' }}>
                          Status: <strong>{isSubmitted.status?.toUpperCase() || 'SUBMITTED'}</strong>
                        </div>
                      </div>
                    )}

                    {/* Action Button */}
                    <button
                      onClick={() => handleSeasonalFormAction(form)}
                      disabled={!isActive || downloading === form.id}
                      style={{
                        width: '100%',
                        background: !isActive ? '#6c757d' : isSubmitted ? '#28a745' : form.google_form_link ? '#007bff' : '#dc3545',
                        color: '#ffffff',
                        border: 'none',
                        padding: '0.75rem 1rem',
                        borderRadius: '6px',
                        cursor: !isActive ? 'not-allowed' : 'pointer',
                        fontSize: '0.9rem',
                        fontWeight: '500',
                        transition: 'opacity 0.3s',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0.5rem',
                        opacity: !isActive ? 0.5 : 1
                      }}
                    >
                      {!isActive ? '⏰ Form Expired' :
                        isSubmitted ? '✅ Submitted' :
                          form.google_form_link ? '🌐 Fill Online Form' :
                            '📥 Download Form'}
                    </button>
                  </div>
                </div>
              );
            })
          ) : (
            // No seasonal forms available
            <div style={{
              gridColumn: '1 / -1',
              background: '#ffffff',
              borderRadius: '12px',
              boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
              padding: '3rem',
              textAlign: 'center',
              color: '#666'
            }}>
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🕒</div>
              <h3 style={{ margin: '0 0 0.5rem 0', color: '#333' }}>No Seasonal Forms Available</h3>
              <p style={{ margin: 0 }}>
                There are currently no active seasonal forms. The HOD will create and enable forms when needed.
              </p>
            </div>
          )
        ) : (
          // Render academic forms (download from public directory)
          getFilteredForms(formsData[selectedCategory]).map((form, index) => (
            <div key={index} style={{
              background: '#ffffff',
              borderRadius: '12px',
              boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
              overflow: 'hidden',
              transition: 'all 0.3s ease',
              cursor: 'pointer'
            }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-5px)';
                e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 15px rgba(0,0,0,0.1)';
              }}>
              {/* Form Header */}
              <div style={{
                background: '#238548',
                color: '#ffffff',
                padding: '1.5rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1rem'
                }}>
                  <span style={{ fontSize: '1.5rem' }}>📄</span>
                  <h3 style={{ margin: 0, fontSize: '1.1rem' }}>{form.name}</h3>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <span style={{
                    padding: '0.25rem 0.75rem',
                    borderRadius: '20px',
                    fontSize: '0.7rem',
                    fontWeight: '500',
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    color: '#ffffff'
                  }}>
                    PDF
                  </span>
                </div>
              </div>

              {/* Form Content */}
              <div style={{ padding: '1.5rem' }}>
                <p style={{
                  margin: '0 0 1rem 0',
                  color: '#666',
                  fontSize: '0.95rem',
                  lineHeight: 1.5
                }}>
                  {form.description}
                </p>

                {/* Instructions */}
                {form.instructions && (
                  <div style={{
                    background: '#f8f9fa',
                    padding: '1rem',
                    borderRadius: '8px',
                    marginBottom: '1rem',
                    border: '1px solid #e9ecef'
                  }}>
                    <h4 style={{ margin: '0 0 0.5rem 0', color: '#495057', fontSize: '0.9rem' }}>
                      📋 Instructions:
                    </h4>
                    <p style={{ margin: 0, fontSize: '0.85rem', color: '#6c757d' }}>
                      {form.instructions}
                    </p>
                  </div>
                )}

                {/* Required Documents */}
                {form.required_documents && (
                  <div style={{
                    background: '#fff3cd',
                    padding: '1rem',
                    borderRadius: '8px',
                    marginBottom: '1rem',
                    border: '1px solid #ffeaa7'
                  }}>
                    <h4 style={{ margin: '0 0 0.5rem 0', color: '#856404', fontSize: '0.9rem' }}>
                      📎 Required Documents:
                    </h4>
                    <p style={{ margin: 0, fontSize: '0.85rem', color: '#856404' }}>
                      {form.required_documents}
                    </p>
                  </div>
                )}

                {/* Form Details */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '1rem',
                  marginBottom: '1.5rem'
                }}>
                  <div>
                    <span style={{
                      display: 'block',
                      fontSize: '0.8rem',
                      color: '#999',
                      marginBottom: '0.25rem'
                    }}>
                      File Size
                    </span>
                    <span style={{
                      fontSize: '0.9rem',
                      fontWeight: '500',
                      color: '#333'
                    }}>
                      {form.size}
                    </span>
                  </div>
                  <div>
                    <span style={{
                      display: 'block',
                      fontSize: '0.8rem',
                      color: '#999',
                      marginBottom: '0.25rem'
                    }}>
                      Last Updated
                    </span>
                    <span style={{
                      fontSize: '0.9rem',
                      fontWeight: '500',
                      color: '#28a745'
                    }}>
                      {form.lastUpdated}
                    </span>
                  </div>
                </div>

                {/* Download Button - Updated for Public Directory */}
                <button
                  onClick={() => handleDownload(form, selectedCategory)}
                  disabled={downloading === form.id}
                  style={{
                    width: '100%',
                    background: downloading === form.id ? '#6c757d' : '#238548',
                    color: '#ffffff',
                    border: 'none',
                    padding: '0.75rem 1rem',
                    borderRadius: '6px',
                    cursor: downloading === form.id ? 'not-allowed' : 'pointer',
                    fontSize: '0.9rem',
                    fontWeight: '500',
                    transition: 'all 0.3s',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem',
                    opacity: downloading === form.id ? 0.7 : 1,
                    transform: downloading === form.id ? 'scale(0.98)' : 'scale(1)'
                  }}
                  onMouseEnter={(e) => {
                    if (downloading !== form.id) {
                      e.target.style.background = '#1e7e34';
                      e.target.style.transform = 'scale(1.02)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (downloading !== form.id) {
                      e.target.style.background = '#238548';
                      e.target.style.transform = 'scale(1)';
                    }
                  }}
                >
                  {downloading === form.id ? (
                    <>
                      <div style={{
                        width: '16px',
                        height: '16px',
                        border: '2px solid #ffffff',
                        borderTop: '2px solid transparent',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                      }}></div>
                      Downloading...
                    </>
                  ) : (
                    <>📥 Download Form</>
                  )}
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* No Results Message */}
      {getFilteredForms(formsData[selectedCategory]).length === 0 && searchTerm && (
        <div style={{
          background: '#ffffff',
          borderRadius: '12px',
          boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
          padding: '3rem',
          textAlign: 'center',
          color: '#666'
        }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🔍</div>
          <h3 style={{ margin: '0 0 0.5rem 0', color: '#333' }}>No forms found</h3>
          <p style={{ margin: 0 }}>No forms match your search criteria. Try a different search term.</p>
        </div>
      )}

      {/* Help Section */}
      <div style={{
        background: '#ffffff',
        borderRadius: '12px',
        boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
        padding: '2rem',
        marginTop: '2rem',
        textAlign: 'center'
      }}>
        <h3 style={{
          margin: '0 0 1rem 0',
          color: '#333',
          fontSize: '1.3rem'
        }}>
          📞 Need Help?
        </h3>
        <p style={{
          margin: '0 0 1rem 0',
          color: '#666',
          fontSize: '1rem',
          lineHeight: 1.6
        }}>
          If you need assistance filling out any form or have questions about the submission process,
          contact our student support team.
        </p>
        <div style={{
          display: 'flex',
          gap: '1rem',
          justifyContent: 'center',
          flexWrap: 'wrap'
        }}>
          <button style={{
            background: '#238548',
            color: '#ffffff',
            border: 'none',
            padding: '0.75rem 1.5rem',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '0.9rem',
            fontWeight: '500',
            transition: 'all 0.3s ease'
          }}>
            📧 Contact Support
          </button>
          <button style={{
            background: '#3498db',
            color: '#ffffff',
            border: 'none',
            padding: '0.75rem 1.5rem',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '0.9rem',
            fontWeight: '500',
            transition: 'all 0.3s ease'
          }}>
            📖 Form Guidelines
          </button>
        </div>
      </div>
    </Layout>
  );
};

export default StudentForm;